// Reminders → Automation Settings. Same idea as the reference product's Timing Settings and
// journey on/off switches, on one screen: turn each automated WhatsApp message on or off, change
// its timing, quiet hours and per-service return days. Nothing lives in env; saving applies at once.
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api, { formatDateTime } from '../services/api';
import { Confirm, Loader, Spinner } from '../components/ui';

const FLOW_ICON = { booking: '📅', idle: '💬', after_service: '💆', missed: '🕒' };

const clone = (v) => JSON.parse(JSON.stringify(v));
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** "= 6 hours", "= 2 days", "= 3 weeks", "about 4 months", or ''. */
function humanize(unit, raw) {
  const n = Number(raw);
  if (!Number.isInteger(n) || n <= 0) return '';
  if (unit === 'minutes' && n >= 60) {
    const h = Math.floor(n / 60);
    const m = n % 60;
    return `= ${plural(h, 'hour')}${m ? ` ${plural(m, 'minute')}` : ''}`;
  }
  if (unit === 'hours' && n >= 24) {
    const d = Math.floor(n / 24);
    const h = n % 24;
    return `= ${plural(d, 'day')}${h ? ` ${plural(h, 'hour')}` : ''}`;
  }
  if (unit === 'days') {
    if (n >= 7 && n % 7 === 0 && n <= 56) return `= ${plural(n / 7, 'week')}`;
    if (n >= 30) {
      const months = Math.round((n / 30.4) * 2) / 2;
      return `about ${months} month${months === 1 ? '' : 's'}`;
    }
  }
  return '';
}

function fieldError(field, raw) {
  if (raw === '' || raw === null || raw === undefined) return 'Enter a number';
  const n = Number(raw);
  if (!Number.isInteger(n)) return 'Whole numbers only';
  if (n < field.min || n > field.max) return `Use ${field.min} to ${field.max} ${field.unit}`;
  return '';
}

/** All problems in the draft, keyed so each input can show its own. */
function validate(draft, steps) {
  const errors = {};
  steps.forEach((step) => {
    step.fields.forEach((f) => {
      const msg = fieldError(f, draft.steps[step.kind][f.key]);
      if (msg) errors[`${step.kind}.${f.key}`] = msg;
    });
  });
  const h24 = Number(draft.steps.reminder_24h?.hours_before);
  const m1 = Number(draft.steps.reminder_1h?.minutes_before);
  if (!errors['reminder_1h.minutes_before'] && !errors['reminder_24h.hours_before'] && m1 >= h24 * 60) {
    errors['reminder_1h.minutes_before'] = 'Must be closer to the appointment than the day-before reminder';
  }
  // Nudges that are on must go out in order: each later than the one before.
  const nudges = steps.filter((s) => s.flow === 'idle' && draft.steps[s.kind]?.enabled);
  nudges.slice(1).forEach((step, i) => {
    const prev = nudges[i];
    const key = `${step.kind}.minutes_after`;
    if (errors[key] || errors[`${prev.kind}.minutes_after`]) return;
    if (Number(draft.steps[step.kind].minutes_after) <= Number(draft.steps[prev.kind].minutes_after)) {
      errors[key] = `Must be later than the ${prev.label.toLowerCase()}`;
    }
  });
  const missed = draft.steps.missed_message;
  const missedFollow = draft.steps.missed_followup;
  if (
    missed?.enabled &&
    missedFollow?.enabled &&
    !errors['missed_message.minutes_after'] &&
    !errors['missed_followup.minutes_after'] &&
    Number(missedFollow.minutes_after) <= Number(missed.minutes_after)
  ) {
    errors['missed_followup.minutes_after'] = 'Must be later than the missed appointment message';
  }
  const ns = draft.no_show;
  if (ns) {
    const m = Number(ns.check_minutes);
    if (ns.check_minutes === '' || !Number.isInteger(m) || m < 5 || m > 720) errors['no_show.check_minutes'] = 'Use 5 to 720 minutes';
    if (!ns.auto_time) errors['no_show.auto_time'] = 'Pick a time';
  }
  const q = draft.quiet_hours;
  if (!q.start) errors['quiet.start'] = 'Pick a time';
  if (!q.end) errors['quiet.end'] = 'Pick a time';
  if (q.start && q.start === q.end) errors['quiet.end'] = 'From and To cannot be the same';
  const seen = new Set();
  draft.service_days.forEach((r, i) => {
    const name = String(r.match || '').trim().toLowerCase();
    if (!name) errors[`svc.${i}.match`] = 'Type a service name';
    else if (seen.has(name)) errors[`svc.${i}.match`] = 'Already in the list';
    seen.add(name);
    const d = Number(r.days);
    if (r.days === '' || !Number.isInteger(d) || d < 1 || d > 365) errors[`svc.${i}.days`] = 'Use 1 to 365 days';
  });
  return errors;
}

/** Draft with numbers as numbers, ready to compare or send. */
function normalize(draft, steps) {
  const out = clone(draft);
  steps.forEach((step) => {
    step.fields.forEach((f) => {
      const v = out.steps[step.kind][f.key];
      out.steps[step.kind][f.key] = v === '' ? v : Number(v);
    });
  });
  if (out.no_show) out.no_show.check_minutes = out.no_show.check_minutes === '' ? '' : Number(out.no_show.check_minutes);
  out.service_days = out.service_days.map((r) => ({ match: String(r.match || '').trim().replace(/\s+/g, ' '), days: r.days === '' ? '' : Number(r.days) }));
  return out;
}

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/** Only what changed, as the backend's partial update. */
function diff(saved, draft) {
  const changes = {};
  const steps = {};
  Object.keys(draft.steps).forEach((kind) => {
    if (!same(saved.steps[kind], draft.steps[kind])) steps[kind] = draft.steps[kind];
  });
  if (Object.keys(steps).length) changes.steps = steps;
  if (!same(saved.quiet_hours, draft.quiet_hours)) changes.quiet_hours = draft.quiet_hours;
  if (!same(saved.service_days, draft.service_days)) changes.service_days = draft.service_days;
  if (draft.no_show && !same(saved.no_show, draft.no_show)) changes.no_show = draft.no_show;
  return changes;
}

function countChanges(changes) {
  return (
    Object.keys(changes.steps || {}).length + (changes.quiet_hours ? 1 : 0) + (changes.service_days ? 1 : 0) + (changes.no_show ? 1 : 0)
  );
}

function clock(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return m ? `${h12}:${String(m).padStart(2, '0')} ${suffix}` : `${h12} ${suffix}`;
}

// ── small pieces ─────────────────────────────────────────────────────────────

function Switch({ on, onChange, label, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      title={on ? `${label}: on. Click to turn off` : `${label}: off. Click to turn on`}
      disabled={disabled}
      className={`nye-switch${on ? ' on' : ''}`}
      onClick={() => onChange(!on)}
    >
      <span className="nye-switch-knob" />
    </button>
  );
}

function NumberBox({ value, onChange, error, disabled, width = 76, label }) {
  return (
    <input
      type="number"
      inputMode="numeric"
      className={`nye-input nye-input--bordered nye-num-box${error ? ' nye-input--error' : ''}`}
      style={{ width }}
      value={value}
      disabled={disabled}
      aria-label={label}
      aria-invalid={Boolean(error)}
      onChange={(e) => onChange(e.target.value === '' ? '' : e.target.value)}
    />
  );
}

function StepRow({ step, value, saved, waiting, template, quietLabel, errors, onChange }) {
  const on = value.enabled;
  const edited = !same(value, saved);
  const turningOff = saved.enabled && !on;
  const fieldErrors = step.fields.map((f) => errors[`${step.kind}.${f.key}`]).filter(Boolean);
  return (
    <div className={`nye-step-row${on ? '' : ' off'}`}>
      <Switch on={on} label={step.label} onChange={(next) => onChange({ ...value, enabled: next })} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="nye-row nye-row--wrap" style={{ gap: 8, alignItems: 'center' }}>
          <span className="nye-strong" style={{ fontSize: 14 }}>
            {step.label}
          </span>
          {on ? null : <span className="nye-pill nye-pill--muted nye-pill--sm">Off</span>}
          {edited ? <span className="nye-pill nye-pill--gold nye-pill--sm">Edited</span> : null}
          <span className="nye-pill nye-pill--square nye-pill--sm nye-mono" title="Approved WhatsApp template">
            {template}
          </span>
          {step.quiet_hours && quietLabel ? (
            <span className="nye-pill nye-pill--sm nye-pill--square nye-pill--info" title="Follows quiet hours">
              🌙 {quietLabel}
            </span>
          ) : null}
          {waiting ? (
            <span className={`nye-pill nye-pill--sm${turningOff ? ' nye-pill--danger' : ' nye-pill--warn'}`}>
              {turningOff ? `${waiting} waiting will stop` : `⏳ ${waiting} waiting`}
            </span>
          ) : null}
        </div>

        {step.fields.length ? (
          <div className="nye-step-sentence">
            {step.fields.map((f) => (
              <React.Fragment key={f.key}>
                {f.before ? <span>{f.before}</span> : null}
                <NumberBox
                  value={value[f.key]}
                  disabled={!on}
                  label={`${step.label} ${f.unit}`}
                  error={errors[`${step.kind}.${f.key}`]}
                  onChange={(v) => onChange({ ...value, [f.key]: v })}
                />
                <span>{f.after}</span>
                {humanize(f.unit, value[f.key]) ? <span className="nye-step-human">{humanize(f.unit, value[f.key])}</span> : null}
              </React.Fragment>
            ))}
          </div>
        ) : null}
        {fieldErrors.length ? <div className="nye-field-error">{fieldErrors[0]}</div> : null}
        {step.note ? <div className="nye-small nye-muted" style={{ marginTop: 4 }}>{step.note}</div> : null}
      </div>
    </div>
  );
}

/** Days after the service on which each return-visit message goes out, for a typical service. */
function ReturnTimeline({ draft }) {
  const s = draft.steps;
  const n = (v) => Number(v);
  const items = [];
  if (s.feedback.enabled) items.push({ day: 0, label: 'Feedback request' });
  const first = n(s.maintenance_initial.days_after);
  if (s.maintenance_initial.enabled && first > 0) items.push({ day: first, label: 'Return visit reminder' });
  const every = n(s.maintenance_followup.every_days);
  const count = n(s.maintenance_followup.count);
  if (s.maintenance_followup.enabled && first > 0 && every > 0 && count > 0) {
    const days = Array.from({ length: Math.min(count, 30) }, (_, i) => first + every * (i + 1));
    items.push({
      day: days[0],
      label: `Return visit nudges ×${count}`,
      detail: count > 1 ? `days ${days[0]}–${days[days.length - 1]}, every ${every}` : '',
    });
  }
  [
    ['maintenance_21d', '3-week check-in'],
    ['maintenance_30d', '1-month check-in'],
    ['maintenance_60d', '2-month check-in'],
    ['maintenance_90d', '3-month check-in'],
    ['rebook_120d', '4-month re-invite'],
    ['rebook_180d', '6-month win-back'],
  ].forEach(([kind, label]) => {
    if (s[kind].enabled && n(s[kind].days_after) > 0) items.push({ day: n(s[kind].days_after), label });
  });
  items.sort((a, b) => a.day - b.day);
  if (!items.length) return <div className="nye-small nye-muted">All return-visit messages are off. Nothing is sent after a service.</div>;
  return (
    <div className="nye-timeline">
      {items.map((it) => (
        <div key={it.label} className="nye-timeline-item">
          <div className="nye-timeline-day">{it.day === 0 ? 'Same day' : `Day ${it.day}`}</div>
          <div className="nye-timeline-label">{it.label}</div>
          {it.detail ? <div className="nye-small nye-muted">{it.detail}</div> : null}
        </div>
      ))}
    </div>
  );
}

// ── screen ───────────────────────────────────────────────────────────────────

export default function AutomationSettings({ onSaved }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'save' | 'reset' | null

  const apply = (res) => {
    setData(res);
    setDraft(clone(res.settings));
  };

  const load = () =>
    api
      .getAutomationSettings()
      .then((res) => {
        apply(res);
        setLoadError('');
      })
      .catch((e) => setLoadError(e.message));

  useEffect(() => {
    load();
  }, []);

  const steps = useMemo(() => data?.steps || [], [data]);
  const normalized = useMemo(() => (draft ? normalize(draft, steps) : null), [draft, steps]);
  const errors = useMemo(() => (normalized ? validate(normalized, steps) : {}), [normalized, steps]);
  const changes = useMemo(() => (data && normalized ? diff(data.settings, normalized) : {}), [data, normalized]);
  const changeCount = countChanges(changes);
  const dirty = changeCount > 0;
  const hasErrors = Object.keys(errors).length > 0;

  // Messages being turned off, and how many waiting copies that stops.
  const turningOff = useMemo(() => {
    if (!data || !draft) return [];
    return steps.filter((s) => data.settings.steps[s.kind].enabled && !draft.steps[s.kind].enabled);
  }, [data, draft, steps]);
  const stopsWaiting = turningOff.reduce((sum, s) => sum + (data?.waiting?.[s.kind] || 0), 0);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  if (loadError) {
    return (
      <div className="nye-callout nye-callout--danger nye-fade">
        <strong>Automation settings could not be loaded.</strong> {loadError}. Restart the demo backend, then open this tab again.
      </div>
    );
  }
  if (!data || !draft) return <Loader text="Loading settings…" />;

  const setStep = (kind, value) => setDraft((d) => ({ ...d, steps: { ...d.steps, [kind]: value } }));
  const setFlow = (flow, enabled) =>
    setDraft((d) => {
      const next = clone(d);
      steps.filter((s) => s.flow === flow).forEach((s) => {
        next.steps[s.kind].enabled = enabled;
      });
      return next;
    });
  const setQuiet = (patch) => setDraft((d) => ({ ...d, quiet_hours: { ...d.quiet_hours, ...patch } }));
  const setNoShow = (patch) => setDraft((d) => ({ ...d, no_show: { ...d.no_show, ...patch } }));
  const setRule = (i, patch) => setDraft((d) => ({ ...d, service_days: d.service_days.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  const addRule = () => setDraft((d) => ({ ...d, service_days: [...d.service_days, { match: '', days: d.steps.maintenance_initial.days_after || 25 }] }));
  const removeRule = (i) => setDraft((d) => ({ ...d, service_days: d.service_days.filter((_, j) => j !== i) }));

  const save = async () => {
    setConfirm(null);
    setSaving(true);
    try {
      const res = await api.saveAutomationSettings(changes);
      apply(res);
      const stopped = Object.values(res.stopped || {}).reduce((a, b) => a + b, 0);
      toast.success(stopped ? `Settings saved. ${plural(stopped, 'waiting message')} stopped.` : 'Settings saved. They apply right away.');
      onSaved?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setConfirm(null);
    setSaving(true);
    try {
      apply(await api.resetAutomationSettings());
      toast.success('Settings are back to the defaults.');
      onSaved?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const requestSave = () => {
    if (!dirty || hasErrors || saving) return;
    if (stopsWaiting > 0) setConfirm('save');
    else save();
  };

  const quiet = draft.quiet_hours;
  const quietLabel = quiet.enabled && quiet.start && quiet.end ? `${clock(quiet.start)}–${clock(quiet.end)}` : '';
  const onCount = steps.filter((s) => draft.steps[s.kind].enabled).length;
  const flows = Object.entries(data.flows);

  return (
    <div className="nye-fade" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="nye-settings-head">
        <div style={{ flex: 1, minWidth: 'min(260px, 100%)' }}>
          <div className="nye-strong" style={{ fontSize: 15 }}>
            Choose which WhatsApp messages go out, and when
          </div>
          <div className="nye-small nye-muted" style={{ marginTop: 3, lineHeight: 1.5 }}>
            Saving applies at once, no restart. Turning a message off also stops the copies already waiting to send. New timings apply to
            messages scheduled after you save; messages already waiting keep their time.
          </div>
        </div>
        <div className="nye-row" style={{ gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
          <span className="nye-pill nye-pill--ok">
            {onCount} of {steps.length} messages on
          </span>
          <button
            type="button"
            className="nye-btn nye-btn--outline nye-btn--sm"
            disabled={saving || (data.is_default && !dirty)}
            onClick={() => setConfirm('reset')}
            title={data.is_default ? 'Already on the defaults' : 'Go back to the default settings'}
          >
            ↺ Reset to defaults
          </button>
        </div>
      </div>

      {flows.map(([flow, title]) => {
        const flowSteps = steps.filter((s) => s.flow === flow);
        const flowOn = flowSteps.filter((s) => draft.steps[s.kind].enabled).length;
        return (
          <React.Fragment key={flow}>
            <div className="nye-card nye-settings-card">
              <div className="nye-row nye-row--between nye-row--wrap" style={{ gap: 10, marginBottom: 4 }}>
                <div className="nye-card-title" style={{ marginBottom: 0 }}>
                  {FLOW_ICON[flow]} {title}
                </div>
                <div className="nye-row" style={{ gap: 8 }}>
                  <span className={`nye-pill nye-pill--sm${flowOn ? ' nye-pill--ok' : ' nye-pill--muted'}`}>
                    {flowOn} of {flowSteps.length} on
                  </span>
                  {flowSteps.length > 1 ? (
                    <button type="button" className="nye-link-btn" onClick={() => setFlow(flow, flowOn < flowSteps.length)}>
                      {flowOn < flowSteps.length ? 'Turn all on' : 'Turn all off'}
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="nye-small nye-muted" style={{ marginBottom: 10 }}>
                {data.flow_triggers?.[flow]}
              </div>
              {flowSteps.map((step) => (
                <StepRow
                  key={step.kind}
                  step={step}
                  value={draft.steps[step.kind]}
                  saved={data.settings.steps[step.kind]}
                  waiting={data.waiting?.[step.kind] || 0}
                  template={data.templates?.[step.kind]}
                  quietLabel={quietLabel}
                  errors={errors}
                  onChange={(v) => setStep(step.kind, v)}
                />
              ))}
              {flow === 'after_service' ? (
                <div className="nye-mt-16">
                  <div className="nye-section-label" style={{ marginBottom: 8 }}>
                    What a customer receives after a service (services not in the list below)
                  </div>
                  <ReturnTimeline draft={normalized} />
                </div>
              ) : null}
            </div>

            {flow === 'missed' && draft.no_show ? (
              <div className="nye-card nye-settings-card">
                <div className="nye-row nye-row--between nye-row--wrap" style={{ gap: 10, marginBottom: 4 }}>
                  <div className="nye-card-title" style={{ marginBottom: 0 }}>
                    ✋ Did they come?
                  </div>
                  {!same(normalized.no_show, data.settings.no_show) ? <span className="nye-pill nye-pill--gold nye-pill--sm">Edited</span> : null}
                </div>
                <div className="nye-small nye-muted" style={{ marginBottom: 10, lineHeight: 1.5 }}>
                  Appointments still Booked after their time. Staff answer with one tap on Appointments; at closing time the rest of today's are
                  marked No-show, which starts the messages above.
                </div>
                <div className="nye-step-row">
                  <Switch on={draft.no_show.check_enabled} label="Ask staff" onChange={(next) => setNoShow({ check_enabled: next })} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="nye-strong">Ask staff "Did they come?"</div>
                    <div className="nye-step-sentence">
                      <span>List appointments still Booked</span>
                      <NumberBox
                        value={draft.no_show.check_minutes}
                        label="Minutes after the start time"
                        disabled={!draft.no_show.check_enabled}
                        error={errors['no_show.check_minutes']}
                        onChange={(v) => setNoShow({ check_minutes: v })}
                      />
                      <span>minutes after their start time, on Appointments and in the sidebar badge.</span>
                    </div>
                    {errors['no_show.check_minutes'] ? <div className="nye-field-error">{errors['no_show.check_minutes']}</div> : null}
                  </div>
                </div>
                <div className="nye-step-row" style={{ borderBottom: 'none' }}>
                  <Switch on={draft.no_show.auto_enabled} label="Automatic No-show" onChange={(next) => setNoShow({ auto_enabled: next })} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="nye-strong">Automatic No-show at closing</div>
                    <div className="nye-step-sentence">
                      <span>At</span>
                      <input
                        type="time"
                        className={`nye-input nye-input--bordered nye-time-box${errors['no_show.auto_time'] ? ' nye-input--error' : ''}`}
                        value={draft.no_show.auto_time}
                        disabled={!draft.no_show.auto_enabled}
                        aria-label="Automatic No-show time"
                        onChange={(e) => setNoShow({ auto_time: e.target.value })}
                      />
                      <span>each day, mark today's appointments nobody updated as No-show.</span>
                    </div>
                    {errors['no_show.auto_time'] ? <div className="nye-field-error">{errors['no_show.auto_time']}</div> : null}
                    <div className="nye-small nye-muted" style={{ marginTop: 6 }}>
                      Earlier days are never marked automatically; they stay in the list for staff.
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {flow === 'after_service' ? (
              <div className="nye-card nye-settings-card">
                <div className="nye-row nye-row--between nye-row--wrap" style={{ gap: 10, marginBottom: 4 }}>
                  <div className="nye-card-title" style={{ marginBottom: 0 }}>
                    ✂️ Return visit timing per service
                  </div>
                  {!same(normalized.service_days, data.settings.service_days) ? <span className="nye-pill nye-pill--gold nye-pill--sm">Edited</span> : null}
                </div>
                <div className="nye-small nye-muted" style={{ marginBottom: 12, lineHeight: 1.5 }}>
                  Some services need a sooner or later first reminder. A service whose name contains the text below uses its own days; when several
                  match, the longest text wins. Every other service uses {plural(Number(normalized.steps.maintenance_initial.days_after) || 0, 'day')}.
                  The nudges that follow count from this date.
                </div>
                {draft.service_days.length ? (
                  <div className="nye-svc-table">
                    <div className="nye-svc-row nye-svc-row--head">
                      <span>Service name contains</span>
                      <span>First reminder after</span>
                      <span />
                    </div>
                    {draft.service_days.map((r, i) => (
                      <div key={i} className="nye-svc-row">
                        <div>
                          <input
                            className={`nye-input nye-input--bordered${errors[`svc.${i}.match`] ? ' nye-input--error' : ''}`}
                            list="nye-known-services"
                            value={r.match}
                            placeholder="e.g. Facial"
                            aria-label="Service name contains"
                            onChange={(e) => setRule(i, { match: e.target.value })}
                          />
                          {errors[`svc.${i}.match`] ? <div className="nye-field-error">{errors[`svc.${i}.match`]}</div> : null}
                        </div>
                        <div>
                          <div className="nye-row" style={{ gap: 8, alignItems: 'center', flexWrap: 'nowrap' }}>
                            <NumberBox
                              value={r.days}
                              label="Days after the service"
                              error={errors[`svc.${i}.days`]}
                              onChange={(v) => setRule(i, { days: v })}
                            />
                            <span className="nye-small">days</span>
                            {humanize('days', r.days) ? <span className="nye-step-human">{humanize('days', r.days)}</span> : null}
                          </div>
                          {errors[`svc.${i}.days`] ? <div className="nye-field-error">{errors[`svc.${i}.days`]}</div> : null}
                        </div>
                        <button type="button" className="nye-icon-action nye-icon-action--delete" title="Remove" aria-label="Remove service" onClick={() => removeRule(i)}>
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="nye-small nye-muted" style={{ marginBottom: 8 }}>
                    No services listed. Every service uses the same first reminder.
                  </div>
                )}
                <datalist id="nye-known-services">
                  {(data.known_services || []).map((name) => (
                    <option key={name} value={name} />
                  ))}
                </datalist>
                <button type="button" className="nye-btn nye-btn--outline nye-btn--sm nye-mt-8" onClick={addRule} style={{ alignSelf: 'flex-start' }}>
                  + Add service
                </button>
              </div>
            ) : null}
          </React.Fragment>
        );
      })}

      <div className="nye-card nye-settings-card">
        <div className="nye-step-row" style={{ borderBottom: 'none', paddingBottom: 0 }}>
          <Switch on={quiet.enabled} label="Quiet hours" onChange={(next) => setQuiet({ enabled: next })} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="nye-row nye-row--wrap" style={{ gap: 8, alignItems: 'center' }}>
              <span className="nye-card-title" style={{ marginBottom: 0, fontSize: 18 }}>
                🌙 Quiet hours
              </span>
              {quiet.enabled ? null : <span className="nye-pill nye-pill--muted nye-pill--sm">Off</span>}
              {!same(normalized.quiet_hours, data.settings.quiet_hours) ? <span className="nye-pill nye-pill--gold nye-pill--sm">Edited</span> : null}
            </div>
            <div className="nye-step-sentence">
              <span>Do not send between</span>
              <input
                type="time"
                className={`nye-input nye-input--bordered nye-time-box${errors['quiet.start'] ? ' nye-input--error' : ''}`}
                value={quiet.start}
                disabled={!quiet.enabled}
                aria-label="Quiet hours from"
                onChange={(e) => setQuiet({ start: e.target.value })}
              />
              <span>and</span>
              <input
                type="time"
                className={`nye-input nye-input--bordered nye-time-box${errors['quiet.end'] ? ' nye-input--error' : ''}`}
                value={quiet.end}
                disabled={!quiet.enabled}
                aria-label="Quiet hours to"
                onChange={(e) => setQuiet({ end: e.target.value })}
              />
              <span>salon time.</span>
            </div>
            {errors['quiet.start'] || errors['quiet.end'] ? <div className="nye-field-error">{errors['quiet.start'] || errors['quiet.end']}</div> : null}
            <div className="nye-small nye-muted" style={{ marginTop: 6, lineHeight: 1.5 }}>
              {quiet.enabled && quiet.end
                ? `A nudge or return-visit message due in this window goes out at ${clock(quiet.end)} instead. `
                : 'Off: nudges and return-visit messages go out at any hour. '}
              Booking confirmations, appointment reminders and feedback requests are never moved.
            </div>
          </div>
        </div>
      </div>

      <div className="nye-small nye-muted" style={{ textAlign: 'right' }}>
        {data.updated_at ? `Last saved ${formatDateTime(data.updated_at)}` : 'Using the default settings'}
      </div>

      {dirty ? (
        <div className="nye-save-bar" role="region" aria-label="Unsaved changes">
          <div style={{ flex: 1, minWidth: 220 }}>
            <div className="nye-strong">
              {plural(changeCount, 'unsaved change')}
            </div>
            <div className="nye-small" style={{ opacity: 0.9 }}>
              {hasErrors
                ? 'Fix the values marked in red to save.'
                : stopsWaiting
                  ? `Turning off ${turningOff.map((s) => s.label).join(', ')} stops ${plural(stopsWaiting, 'message')} waiting to send.`
                  : 'Applies as soon as you save.'}
            </div>
          </div>
          <button type="button" className="nye-btn nye-btn--ghost-light nye-btn--sm" disabled={saving} onClick={() => setDraft(clone(data.settings))}>
            Discard
          </button>
          <button type="button" className="nye-btn nye-btn--gold nye-btn--sm" disabled={saving || hasErrors} onClick={requestSave}>
            {saving ? <Spinner /> : null} Save changes
          </button>
        </div>
      ) : null}

      {confirm === 'save' ? (
        <Confirm
          title="Stop waiting messages?"
          message={`You are turning off ${turningOff.map((s) => s.label).join(', ')}. ${plural(stopsWaiting, 'message')} already waiting to send will be stopped. Turning it back on later does not bring them back; it applies to new bookings and services.`}
          confirmLabel="Save and stop them"
          onConfirm={save}
          onCancel={() => setConfirm(null)}
        />
      ) : null}
      {confirm === 'reset' ? (
        <Confirm
          title="Reset to defaults?"
          message="Every message is turned on, timings go back to the standard values (6-hour nudge, return visit after 25 days, Nail services 20 days) and quiet hours go back to 8 PM–8 AM. Unsaved changes are discarded. Messages already waiting keep their time."
          confirmLabel="Reset to defaults"
          danger={false}
          onConfirm={reset}
          onCancel={() => setConfirm(null)}
        />
      ) : null}
    </div>
  );
}
